/**
 * EnquirySheet — structured enquiry modal opened from /trainers/:id.
 * Posts to /api/pro/enquiry which creates a message thread + auto-message.
 */
import { useState } from "react";
import { motion } from "framer-motion";
import { X } from "lucide-react";
import { apiClient } from "../../services/api";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";

const GOALS = [
  { id: "weight_loss", label: "Weight loss" },
  { id: "muscle_gain", label: "Muscle gain" },
  { id: "general_fitness", label: "General fitness" },
  { id: "nutrition_plan", label: "Nutrition plan" },
  { id: "injury_rehab", label: "Injury rehab" },
  { id: "other", label: "Other" },
];

const TIME_PREFS = [
  { id: "morning", label: "Morning" },
  { id: "afternoon", label: "Afternoon" },
  { id: "evening", label: "Evening" },
  { id: "flexible", label: "Flexible" },
];

const SESSION_TYPES = [
  { id: "online", label: "Online" },
  { id: "in_person", label: "In-person" },
  { id: "either", label: "Either" },
];

export default function EnquirySheet({ professionalId, professionalName, onClose }) {
  const navigate = useNavigate();
  const [goals, setGoals] = useState([]);
  const [sessionType, setSessionType] = useState("online");
  const [timePref, setTimePref] = useState("flexible");
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);

  const toggleGoal = (g) => {
    setGoals((prev) => (prev.includes(g) ? prev.filter((x) => x !== g) : [...prev, g]));
  };

  const submit = async () => {
    if (goals.length === 0) return toast.error("Pick at least one goal");
    setBusy(true);
    try {
      const { data } = await apiClient.post("/pro/enquiry", {
        professional_user_id: professionalId,
        goals,
        session_type: sessionType,
        time_preference: timePref,
        message: message.trim() || null,
      });
      toast.success(`Enquiry sent. ${professionalName?.split(" ")[0]} will respond shortly.`);
      onClose?.();
      // Optionally navigate to the new thread
      if (data?.thread_id) {
        // skip auto-nav for now to avoid surprising the user
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not send enquiry");
    } finally {
      setBusy(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-[70] grid place-items-end bg-black/45"
      data-testid="enquiry-sheet"
      onClick={onClose}
    >
      <motion.div
        initial={{ y: "100%" }}
        animate={{ y: 0 }}
        transition={{ duration: 0.25 }}
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-2xl bg-white rounded-t-[24px] p-5"
        style={{ maxHeight: "90vh", overflowY: "auto" }}
      >
        <div className="flex items-center justify-between">
          <h2 className="font-display text-[22px]">Send enquiry</h2>
          <button onClick={onClose} aria-label="Close" className="w-8 h-8 grid place-items-center rounded-full bg-canvas2">
            <X size={16} />
          </button>
        </div>

        <Field label="What are you looking for?">
          <div className="flex flex-wrap gap-2">
            {GOALS.map((g) => {
              const on = goals.includes(g.id);
              return (
                <button
                  key={g.id}
                  onClick={() => toggleGoal(g.id)}
                  data-testid={`goal-${g.id}`}
                  className="px-3 h-9 rounded-pill text-[12px]"
                  style={{
                    background: on ? "#00D4FF" : "#ECEAE7",
                    color: on ? "#0B0B0B" : "#6B6760",
                    fontWeight: on ? 700 : 600,
                    borderRadius: 100,
                  }}
                >
                  {g.label}
                </button>
              );
            })}
          </div>
        </Field>

        <Field label="Preferred session type">
          <div className="flex gap-2">
            {SESSION_TYPES.map((t) => {
              const on = sessionType === t.id;
              return (
                <button
                  key={t.id}
                  onClick={() => setSessionType(t.id)}
                  data-testid={`session-${t.id}`}
                  className="flex-1 h-10 rounded-pill text-[13px]"
                  style={{
                    background: on ? "#00D4FF" : "#ECEAE7",
                    color: on ? "#0B0B0B" : "#6B6760",
                    fontWeight: on ? 700 : 600,
                    borderRadius: 100,
                  }}
                >
                  {t.label}
                </button>
              );
            })}
          </div>
        </Field>

        <Field label="Preferred time (optional)">
          <div className="flex gap-2 flex-wrap">
            {TIME_PREFS.map((t) => {
              const on = timePref === t.id;
              return (
                <button
                  key={t.id}
                  onClick={() => setTimePref(t.id)}
                  data-testid={`time-${t.id}`}
                  className="px-3 h-9 rounded-pill text-[12px]"
                  style={{
                    background: on ? "#00D4FF" : "#ECEAE7",
                    color: on ? "#0B0B0B" : "#6B6760",
                    fontWeight: on ? 700 : 600,
                    borderRadius: 100,
                  }}
                >
                  {t.label}
                </button>
              );
            })}
          </div>
        </Field>

        <Field label="Message (optional)">
          <textarea
            value={message}
            onChange={(e) => setMessage(e.target.value.slice(0, 200))}
            placeholder="Any additional context…"
            data-testid="enquiry-message"
            className="w-full p-3 rounded-[12px] border border-line bg-canvas2 outline-none"
            style={{ minHeight: 80 }}
          />
          <div className="text-right text-[11px] text-ink-soft mt-0.5">{message.length}/200</div>
        </Field>

        <button
          onClick={submit}
          disabled={busy || goals.length === 0}
          data-testid="enquiry-submit"
          className="w-full h-12 rounded-pill mt-3 disabled:opacity-50"
          style={{ background: "#00D4FF", color: "#000", borderRadius: 100, fontWeight: 700 }}
        >
          {busy ? "Sending…" : "Send Enquiry"}
        </button>
      </motion.div>
    </motion.div>
  );
}

function Field({ label, children }) {
  return (
    <div className="mt-5">
      <div className="text-[11px] uppercase tracking-wider text-ink-tertiary font-medium mb-2">{label}</div>
      {children}
    </div>
  );
}
